/**
 * 時間前市場の未開場判定。
 *
 * 受渡日前日 17:00 が開場時刻 (= mock IntradayMarket.razor:56 と整合)。
 * frontend client-side の表示 gate のみ。バックエンドの業務ロジックとは独立。
 *
 * 開場時刻も GC と同じく **JST の壁時計**で定義される業務概念なので、 受渡日の暦日を
 * JST として絶対時刻に落として `now` と比べる (#4765。 ローカル時刻で組むと非 JST
 * 端末で開場が最大 9 時間ズレ、 GC 判定とも食い違う)。
 */
import {
  addDays,
  formatDateDisplay,
  jstWallClockToInstant,
} from '~/models/dateTime'
import {
  INTRADAY_MARKET_OPEN_HOUR,
  INTRADAY_MARKET_OPEN_MINUTE,
} from '~/pages/IntradayBidding/constants/market'

/**
 * 受渡日前日 17:00 (JST) の絶対時刻 (= 時間前市場の開場時刻)。 戦略モードの
 * スケジュール適用日時の下限 (AC-BIN-005b、 `strategySchedule.ts`) にも使う。
 */
export const marketOpenAt = (deliveryDate: Date): Date =>
  jstWallClockToInstant(
    addDays(deliveryDate, -1),
    INTRADAY_MARKET_OPEN_HOUR,
    INTRADAY_MARKET_OPEN_MINUTE,
  )

/**
 * 時間前市場が未開場かどうかを判定する。
 *
 * @param now - 現在時刻 (= 絶対時刻)
 * @param deliveryDate - 受渡日 (= カレンダー日 `Date`、 `models/dateTime` 冒頭の定義)
 * @returns true = 未開場 (= market-body を無効化)
 */
export function isMarketClosed(now: Date, deliveryDate: Date): boolean {
  return now.getTime() < marketOpenAt(deliveryDate).getTime()
}

/**
 * 開場時刻文字列を返す (= 未開場メッセージ表示用)。
 *
 * 表示も JST 壁時計 (= 定数そのまま)。 絶対時刻を経由してローカル整形すると非 JST
 * 端末で判定 ([[isMarketClosed]]) と食い違う文字列が出る。
 *
 * @param deliveryDate - 受渡日
 * @param language - 画面の表示言語
 * @returns 日本語は "YYYY/MM/DD HH:MM"、 英語は "YYYY-MM-DD HH:MM"
 */
export function marketOpenTimeLabel(
  deliveryDate: Date,
  language = 'ja',
): string {
  const h = String(INTRADAY_MARKET_OPEN_HOUR).padStart(2, '0')
  const m = String(INTRADAY_MARKET_OPEN_MINUTE).padStart(2, '0')
  return `${formatDateDisplay(addDays(deliveryDate, -1), language)} ${h}:${m}`
}
